import _ from 'lodash';
import React from 'react';
import { NavLink } from 'fluxible-router';
import shallowCompare from '../../lib/shallowCompare';
import Customer from '../../models/Customer';
import InventoryRecord from '../../models/InventoryRecord';
import OrderItem from '../../models/OrderItem';
import Store from '../../models/Store';
import ShoppingCartActions from '../../actions/ShoppingCartActions';
import DelectableRating from '../DelectableRating';
import ZipCodeModal from '../ZipCodeModal';
import Image from '../Image';
import MinusPng from '../images/quantity_decrease.png';
import MinusSvg from '../images/quantity_decrease.svg';
import PlusPng from '../images/quantity_increase.png';
import PlusSvg from '../images/quantity_increase.svg';
import styles from './index.sass';

class InventoryRecordCard extends React.Component {
  static propTypes = {
    inventoryRecord: React.PropTypes.instanceOf(InventoryRecord).isRequired,
    item: React.PropTypes.instanceOf(OrderItem),
    store: React.PropTypes.instanceOf(Store).isRequired,
    customer: React.PropTypes.instanceOf(Customer).isRequired
  };

  static contextTypes = {
    executeAction: React.PropTypes.func
  };

  constructor(props) {
    super(props);

    this.state = {
      quantity: props.item ? props.item.quantity : 0,
      quantityShakeAnimation: false,
      showZipCodeModal: false,
      initiatedUpdate: false
    };
    this.updateQuantity = _.debounce(this._updateQuantity, 300);
  }

  componentWillReceiveProps(nextProps) {
    if(nextProps.item !== this.props.item) {
      this.setState({quantity: nextProps.item ? nextProps.item.quantity : 0});
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  componentDidUpdate(prevProps, prevState) {
    if(this.state.initiatedUpdate && this.state.quantity !== prevState.quantity) {
      if(prevState.quantity === 0) {
        this.addToCart();
      } else {
        this.updateQuantity();
      }
    }
  }

  _updateQuantity() {
    let quantity = this.state.quantity;
    if(!this.props.item || quantity === this.props.item.quantity) {return;}
    if(quantity === 0) {return this.removeFromCart();}
    this.setState({initiatedUpdate: false});
    this.context.executeAction(ShoppingCartActions.updateItem, {
      store_id : this.props.store.id,
      item_id  : this.props.item.id,
      quantity : quantity,
      inventoryRecord: this.props.inventoryRecord
    });
  }

  addToCart() {
    this.setState({initiatedUpdate: false});
    this.context.executeAction(ShoppingCartActions.addItem, {
      store_id: this.props.store.id,
      inventory_record_id: this.props.inventoryRecord.id,
      quantity: 1,
      inventoryRecord: this.props.inventoryRecord
    });
  }

  removeFromCart() {
    this.setState({initiatedUpdate: false});
    this.context.executeAction(ShoppingCartActions.removeItem, {
      store_id: this.props.store.id,
      item_id  : this.props.item.id,
      inventoryRecord: this.props.inventoryRecord
    });
  }

  handleOnClickBuyButton(e) {
    e.stopPropagation();
    e.preventDefault();

    let customer = this.props.customer;
    if(!customer.preferences.zip_code) {
      return this.setState({showZipCodeModal: true});
    }

    this.setState({
      quantity: 1,
      initiatedUpdate: true
    });
  }

  handleOnClickDecrementButton(e) {
    e.stopPropagation();
    e.preventDefault();

    this.setState((prevState) => {
      return {
        quantity: prevState.quantity - 1,
        initiatedUpdate: true
      };
    });
  }

  handleOnClickIncrementButton(e) {
    e.stopPropagation();
    e.preventDefault();

    let maxPurchaseQuantity = this.props.inventoryRecord.max_purchase_quantity;
    if(maxPurchaseQuantity && this.state.quantity === maxPurchaseQuantity) {
      this.setState({quantityShakeAnimation: true});
      setTimeout(() => {
        this.setState({quantityShakeAnimation: false});
      }, 100);
      return;
    }

    this.setState((prevState) => {
      return {
        quantity: prevState.quantity + 1,
        initiatedUpdate: true
      };
    });
  }

  handleOnCloseZipCodeModal(zipCodeAdded) {
    this.setState({showZipCodeModal: false});
    if(!zipCodeAdded) {return;}
    this.setState({
      quantity: 1,
      initiatedUpdate: true
    });
  }

  renderJSONLD(inventoryRecord) {
    let json = inventoryRecord.generateSchemaJSON({store: this.props.store});
    return <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(json)}} />;
  }

  renderZipCodeModal() {
    if(!this.state.showZipCodeModal) {return null;}
    let zipCodeModalProps = {
      onClose: this.handleOnCloseZipCodeModal.bind(this)
    };
    return <ZipCodeModal {...zipCodeModalProps} />;
  }

  render() {
    let { inventoryRecord } = this.props;
    let { quantity, quantityShakeAnimation } = this.state;
    let delectableRating = inventoryRecord.delectable_rating;
    if(delectableRating) {
      let delectableRatingProps = {
        rating: delectableRating.object.average_rating,
        count: delectableRating.object.ratings_count,
        style: {display: 'block', whiteSpace: 'pre', marginTop: 12}
      };
      delectableRating = <DelectableRating {...delectableRatingProps} />;
    }
    let imageProps = {
      className: styles.photo,
      tag: 'div',
      aspectRatio: (288/228),
      targetWidth: 288,
      photoHash: inventoryRecord.photo_hash,
      borderRadius: '4px 4px 0 0'
    };
    let priceText = inventoryRecord.price_text;
    let salePriceText = inventoryRecord.sale_price_text;
    let availability = this.props.inventoryRecord.availability;

    let price = (<p className={styles.price}><span>{priceText}</span></p>);
    if(salePriceText) {
      price = (
        <p className={styles.price}>
          {inventoryRecord.sale_discount_text ? <span className={styles.discount}>{inventoryRecord.sale_discount_text}</span> : null}
          {inventoryRecord.sale_discount_text ? <br/> : null}
          <span className={styles.strikethrough}>{priceText}</span>
          <span>{salePriceText}</span>
        </p>
      );
    }

    let buyButtonProps = {
      className: styles.buyButton,
      onClick: this.handleOnClickBuyButton.bind(this)
    };
    let buyButtonText = 'Add to bag';
    if(availability === 'sold_out') {
      buyButtonProps.disabled = true;
      buyButtonText = 'Sold out';
    } else if(availability === 'unavailable') {
      buyButtonProps.disabled = true;
      buyButtonText = 'Unavailable';
    }
    let buyButton = <button {...buyButtonProps}>{buyButtonText}</button>;
    let decrementQuantityButtonProps = {
      className: styles.decrementQuantityButton,
      onClick: this.handleOnClickDecrementButton.bind(this)
    };
    let incrementQuantityButtonProps = {
      className: styles.incrementQuantityButton,
      onClick: this.handleOnClickIncrementButton.bind(this)
    };

    return (
      <NavLink className={styles.inventoryRecordCard} routeName="inventoryRecord" navParams={{id: inventoryRecord.id, slug: inventoryRecord.slug}}>
        <Image {...imageProps} />
        <div className={`${styles.hoverOverlay} ${quantity ? styles.hasInBag : ''}`}>
          {buyButton}
          <span className={`${styles.quantity} ${quantityShakeAnimation ? styles.shake : ''}`}>{quantity}</span>
          <span className={styles.quantitySubtext}>in your bag</span>
          <div className={styles.quantityButtons}>
            <button {...decrementQuantityButtonProps}><img src={MinusPng} srcSet={MinusSvg} /></button>
            <button {...incrementQuantityButtonProps}><img src={PlusPng} srcSet={PlusSvg} /></button>
          </div>
        </div>
        <div className={styles.details}>
          <p className={styles.name}>{inventoryRecord.brand} {inventoryRecord.name}</p>
          {delectableRating}
          {price}
        </div>
        {this.renderJSONLD(inventoryRecord)}
        {this.renderZipCodeModal()}
      </NavLink>
    );
  }
}

export default InventoryRecordCard;



// WEBPACK FOOTER //
// banquet/components/InventoryRecordCard/index.js